import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useMemo, useState } from "react";
import useBangKeoMatches from "@/hooks/useBangKeoMatches";
import LoadingInner from "@/components/Loading";
import NoData from "@/components/NoData";
import hotIcon from "@/assets/images/hot.svg";
import "./index.css";

const HOT_LEAGUE_ID = "1572";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";
const COLLAPSED_COUNT = 10;

const normalizeScore = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  return Number.isNaN(num) ? "-" : String(num);
};

const ResultRow = ({ match, isLast }) => {
  const leagueName = match.league?.nameVN || match.league?.name || "";
  const isHotLeague = String(match.league?.id) === HOT_LEAGUE_ID;
  const matchTimeStr =
    match?.matchTime != null ? dayjs.unix(match.matchTime).tz(TZ).format("HH:mm") : "--";
  const statusNum = Number(match?.status ?? 0);
  const isNotStarted = statusNum === 0;
  const homeScore = normalizeScore(match?.homeScore);
  const awayScore = normalizeScore(match?.awayScore);
  const homeName = match.homeTeam?.nameVi || match.homeName || "-";
  const awayName = match.awayTeam?.nameVi || match.awayName || "-";

  return (
    <div className="kbv-ltd-v4-row relative px-3 py-2">
      <div className="mb-2 flex items-center gap-2 min-w-0">
        {match.league?.logo && (
          <img
            src={match.league.logo}
            alt={leagueName}
            className="w-5 h-5 object-contain shrink-0"
            loading="lazy"
          />
        )}
        <div className="flex items-center gap-1 min-w-0">
          <span
            className={`text-sm font-bold leading-5 truncate ${isHotLeague ? "text-[var(--kbv-ltd-v4-hot-league,#d97706)]" : "text-[var(--theme-subtext,var(--theme-text-muted))]"}`}
            title={leagueName}
          >
            {leagueName}
          </span>
          {isHotLeague ? (
            <img src={hotIcon} alt="hot" className="h-4 w-auto shrink-0" loading="lazy" />
          ) : null}
        </div>
      </div>
      <span className="mb-1.5 block kbv-ltd-v4-time text-sm font-bold tabular-nums text-[var(--kbv-ltd-v4-time,var(--theme-primary))] leading-5">
        {matchTimeStr}
      </span>
      <div className="flex items-center gap-1.5 min-w-0">
          <p
            className="flex-1 min-w-0 m-0 line-clamp-1 text-right text-sm font-semibold leading-5 text-[var(--theme-text)]"
            title={homeName}
          >
            {homeName}
          </p>
          {match.homeTeam?.logo ? (
            <img
              src={match.homeTeam.logo}
              alt={match.homeName}
              className="w-5 h-5 rounded-full object-cover shrink-0"
              loading="lazy"
            />
          ) : null}
          <div className="flex items-center shrink-0 px-0.5">
            {isNotStarted ? (
              <span className="text-sm font-bold leading-5 text-[var(--theme-text-muted)] uppercase">
                VS
              </span>
            ) : (
              <>
                <span className="text-sm font-bold leading-5 text-[var(--theme-primary)] tabular-nums">
                  {homeScore}
                </span>
                <span className="text-xs font-medium text-[var(--theme-text-muted)] mx-0.5">-</span>
                <span className="text-sm font-bold leading-5 text-[var(--theme-primary)] tabular-nums">
                  {awayScore}
                </span>
              </>
            )}
          </div>
          {match.awayTeam?.logo ? (
            <img
              src={match.awayTeam.logo}
              alt={match.awayName}
              className="w-5 h-5 rounded-full object-cover shrink-0"
              loading="lazy"
            />
          ) : null}
          <p
            className="flex-1 min-w-0 m-0 line-clamp-1 text-left text-sm font-semibold leading-5 text-[var(--theme-text)]"
            title={awayName}
          >
            {awayName}
          </p>
      </div>
      {!isLast ? <span className="absolute left-3 right-3 bottom-0 h-px bg-[var(--theme-border)]" /> : null}
    </div>
  );
};

const LichThiDauKQV4 = ({ type = "all", themeStyle = {} }) => {
  const todayDate = dayjs().tz(TZ);
  const minDate = todayDate.format("YYYY-MM-DD");
  const maxDate = todayDate.add(7, "day").format("YYYY-MM-DD");
  const [selectedDate, setSelectedDate] = useState(() => minDate);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleDateChange = (event) => {
    const nextDate = event.target.value;
    if (!nextDate || nextDate < minDate || nextDate > maxDate) return;
    setSelectedDate(nextDate);
    setIsExpanded(false);
  };

  const queryObject = useMemo(
    () => ({
      include: "homeTeam,awayTeam,odd,countOtherOdds,stats,events,insight",
      filterInclude: `date:${selectedDate}`,
      filterOdds: -1,
      cache: false,
      status: type === "ketqua" ? "end" : "",
      page: 1,
      limit: 20,
    }),
    [selectedDate, type],
  );

  const { leaguesWithMatches, loading } = useBangKeoMatches(queryObject, false);

  const matches = useMemo(
    () =>
      leaguesWithMatches
        .flatMap((leagueItem) =>
          (leagueItem.matches || []).map((match) => ({
            ...match,
            league: leagueItem.league || match.league,
          })),
        )
        .sort((a, b) => (a.matchTime ?? 0) - (b.matchTime ?? 0)),
    [leaguesWithMatches],
  );
  const visibleMatches = useMemo(
    () => (isExpanded ? matches : matches.slice(0, COLLAPSED_COUNT)),
    [isExpanded, matches],
  );
  const canToggleExpand = matches.length > COLLAPSED_COUNT;

  return (
    <div
      className="kbv-ltd-v4 w-full max-w-full rounded-xl border border-[var(--kbv-card-border,var(--theme-border))] bg-[var(--kbv-card-bg,var(--theme-bg))] overflow-hidden shadow-sm"
      style={themeStyle}
    >
      <div className="flex items-center justify-between gap-2 px-3 py-3 border-b border-[var(--theme-border)]">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className="kbv-ltd-v4-accent w-1 h-5 shrink-0 rounded-full bg-[var(--theme-primary)]" aria-hidden="true" />
          <h3 className="text-base font-bold uppercase tracking-tight truncate text-[var(--theme-text)]">Lịch Thi Đấu</h3>
        </div>
        <input
          type="date"
          value={selectedDate}
          min={minDate}
          max={maxDate}
          onChange={handleDateChange}
          className="kbv-ltd-v4-date-input shrink-0 rounded-md border border-[var(--theme-border)] bg-[var(--theme-bg)] px-2 py-1 text-xs font-medium text-[var(--theme-text)] outline-none transition-colors focus:border-[var(--theme-primary)] [color-scheme:light]"
          aria-label="Chọn ngày xem lịch thi đấu"
        />
      </div>
      {loading && <LoadingInner />}
      {!loading && (
        <div className="w-full">
          {matches.length === 0 ? (
            <div className="p-3">
              <NoData message="Không có trận đấu nào" />
            </div>
          ) : (
            <>
              {visibleMatches.map((match, index) => (
                <ResultRow
                  key={match.id}
                  match={match}
                  isLast={index === visibleMatches.length - 1 && !canToggleExpand}
                />
              ))}
              {canToggleExpand && (
                <div className="px-3 py-2.5 border-t border-[var(--theme-border)] bg-[var(--kbv-card-bg,var(--theme-bg))]">
                  <button
                    type="button"
                    onClick={() => setIsExpanded((prev) => !prev)}
                    className="kbv-ltd-v4-expand-btn w-full text-sm font-medium text-[var(--theme-subtext,var(--theme-text-muted))] hover:text-[var(--theme-text)] transition-colors"
                  >
                    {isExpanded ? "Thu gọn" : "Mở rộng"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default LichThiDauKQV4;
